import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import dagre from '@dagrejs/dagre'
import { Position, useNodesInitialized, useReactFlow } from '@xyflow/react'

import { EdgeUI, NodeUI } from 'modules/workflows/components/Node/types'

/**
 * Dagre ignores insertion order and may place condition branches in arbitrary horizontal order.
 * This function reorders them so conditionIf branches appear left-to-right by priority,
 * and conditionElse is always rightmost.
 *
 * Note: subtree alignment (keeping descendants vertically under their branch) is handled
 * upstream via edge weights — see CONDITION_BRANCH_EDGE_WEIGHT.
 */
const reorderConditionBranches = (
  graph: dagre.graphlib.Graph,
  nodesToLayout: NodeUI[],
  edgesToLayout: EdgeUI[]
): void => {
  const nodeMap = new Map(nodesToLayout.map((n) => [n.id, n]))

  const branchGroups = new Map<string, NodeUI[]>()
  edgesToLayout.forEach((e) => {
    const source = nodeMap.get(e.source)
    const target = nodeMap.get(e.target)
    if (
      source?.type === 'condition' &&
      target &&
      (target.type === 'conditionIf' || target.type === 'conditionElse')
    ) {
      const group = branchGroups.get(e.source) || []
      group.push(target)
      branchGroups.set(e.source, group)
    }
  })

  if (branchGroups.size === 0) return

  const outgoing = new Map<string, string[]>()
  edgesToLayout.forEach((e) => {
    const targets = outgoing.get(e.source) || []
    targets.push(e.target)
    outgoing.set(e.source, targets)
  })

  branchGroups.forEach((branches) => {
    if (branches.length <= 1) return

    const sortedXPositions = branches.map((b) => graph.node(b.id).x).sort((a, b) => a - b)

    const sortedBranches = [...branches].sort((a, b) => {
      if (a.type === 'conditionElse') return 1
      if (b.type === 'conditionElse') return -1
      const pA = a.type === 'conditionIf' ? a.data.comparison.priority : 0
      const pB = b.type === 'conditionIf' ? b.data.comparison.priority : 0
      return pA - pB
    })

    sortedBranches.forEach((branch, index) => {
      const branchNode = graph.node(branch.id)
      const targetX = sortedXPositions[index]
      if (!branchNode || targetX === undefined) return

      const dx = targetX - branchNode.x
      if (Math.abs(dx) < 0.5) return

      // Move the branch and every node underneath it
      branchNode.x = targetX
      const visited = new Set<string>()
      const queue = outgoing.get(branch.id)?.slice() || []
      let id = queue.shift()
      while (id) {
        if (!visited.has(id)) {
          visited.add(id)
          const n = graph.node(id)
          if (n) n.x += dx
          queue.push(...(outgoing.get(id) || []))
        }
        id = queue.shift()
      }
    })
  })
}

/**
 * After branch reordering, subtrees of adjacent branches may overlap horizontally
 * (e.g. a nested condition fans out wider than its parent branch slot).
 * This walks each condition's branches left-to-right and pushes apart any that overlap.
 * Conditions are processed deepest-first so inner overlaps are resolved before
 * outer subtree bounds are computed.
 */
const resolveConditionBranchOverlaps = (
  graph: dagre.graphlib.Graph,
  nodesToLayout: NodeUI[],
  edgesToLayout: EdgeUI[],
  nodesep: number
): void => {
  const nodeMap = new Map(nodesToLayout.map((n) => [n.id, n]))

  const branchGroups = new Map<string, NodeUI[]>()
  edgesToLayout.forEach((e) => {
    const source = nodeMap.get(e.source)
    const target = nodeMap.get(e.target)
    if (
      source?.type === 'condition' &&
      target &&
      (target.type === 'conditionIf' || target.type === 'conditionElse')
    ) {
      const group = branchGroups.get(e.source) || []
      group.push(target)
      branchGroups.set(e.source, group)
    }
  })

  if (branchGroups.size === 0) return

  const outgoing = new Map<string, string[]>()
  edgesToLayout.forEach((e) => {
    const targets = outgoing.get(e.source) || []
    targets.push(e.target)
    outgoing.set(e.source, targets)
  })

  /** BFS: collect every node id in a subtree (with cycle protection). */
  const collectSubtreeIds = (rootId: string): string[] => {
    const ids: string[] = []
    const visited = new Set<string>()
    const queue: string[] = [rootId]

    while (queue.length > 0) {
      const current = queue.shift()
      if (!current || visited.has(current)) continue
      visited.add(current)
      ids.push(current)

      const children = outgoing.get(current) || []
      for (const child of children) {
        if (!visited.has(child)) {
          queue.push(child)
        }
      }
    }
    return ids
  }

  /** Horizontal bounding box of an entire subtree. */
  const getSubtreeBounds = (rootId: string): { left: number; right: number } => {
    let left = Infinity
    let right = -Infinity
    for (const id of collectSubtreeIds(rootId)) {
      const node = graph.node(id)
      if (node) {
        left = Math.min(left, node.x - node.width / 2)
        right = Math.max(right, node.x + node.width / 2)
      }
    }
    return { left, right }
  }

  /** Shift every node in a subtree by dx. */
  const shiftSubtree = (rootId: string, dx: number): void => {
    for (const id of collectSubtreeIds(rootId)) {
      const node = graph.node(id)
      if (node) node.x += dx
    }
  }

  // Process deepest conditions first (highest Y in TB layout)
  const sortedConditions = [...branchGroups.entries()].sort(
    ([a], [b]) => (graph.node(b)?.y ?? 0) - (graph.node(a)?.y ?? 0)
  )

  sortedConditions.forEach(([conditionId, branches]) => {
    if (branches.length <= 1) return

    const sorted = [...branches].sort(
      (a, b) => (graph.node(a.id)?.x ?? 0) - (graph.node(b.id)?.x ?? 0)
    )

    // Walk left-to-right, pushing apart when subtrees overlap
    for (let i = 1; i < sorted.length; i++) {
      const leftBranch = sorted[i - 1]
      const rightBranch = sorted[i]
      if (!leftBranch || !rightBranch) continue

      const leftBounds = getSubtreeBounds(leftBranch.id)
      const rightBounds = getSubtreeBounds(rightBranch.id)
      const gap = rightBounds.left - leftBounds.right

      if (gap < nodesep) {
        const shift = nodesep - gap
        // Push this branch and every branch to its right
        for (let j = i; j < sorted.length; j++) {
          const branchToShift = sorted[j]
          if (branchToShift) shiftSubtree(branchToShift.id, shift)
        }
      }
    }

    // Re-balance: shift ALL branches so their center stays under the condition node.
    // This keeps the condition (and everything above it) in place instead of moving
    // the condition rightward, which would misalign it with its parent nodes.
    const condNode = graph.node(conditionId)
    const firstBranch = sorted[0]
    const lastBranch = sorted[sorted.length - 1]
    if (condNode && firstBranch && lastBranch) {
      const firstX = graph.node(firstBranch.id)?.x
      const lastX = graph.node(lastBranch.id)?.x
      if (firstX !== undefined && lastX !== undefined) {
        const currentCenter = (firstX + lastX) / 2
        const rebalanceDx = condNode.x - currentCenter
        if (Math.abs(rebalanceDx) > 0.5) {
          sorted.forEach((branch) => {
            shiftSubtree(branch.id, rebalanceDx)
          })
        }
      }
    }
  })
}

/**
 * Recomputes Y positions so that every node is placed exactly `ranksep` below
 * the bottom edge of its tallest predecessor. This eliminates the uneven gaps
 * that dagre creates when nodes of different heights share a rank.
 *
 * Uses Kahn's topological sort so parents are always positioned before children.
 * Dagre's X positions are kept; only Y is replaced.
 */
const computeConsistentYPositions = (
  edges: EdgeUI[],
  nodeIds: string[],
  heights: Record<string, number>,
  defaultNodeHeight: number,
  ranksep: number
): Map<string, number> => {
  const incomingSources = new Map<string, string[]>()
  const outgoingTargets = new Map<string, string[]>()
  edges.forEach((e) => {
    const inc = incomingSources.get(e.target) ?? []
    inc.push(e.source)
    incomingSources.set(e.target, inc)
    const out = outgoingTargets.get(e.source) ?? []
    out.push(e.target)
    outgoingTargets.set(e.source, out)
  })

  const inDegree = new Map<string, number>()
  nodeIds.forEach((id) => inDegree.set(id, 0))
  edges.forEach((e) => {
    inDegree.set(e.target, (inDegree.get(e.target) ?? 0) + 1)
  })

  const queue = nodeIds.filter((id) => (inDegree.get(id) ?? 0) === 0)
  const topOrder: string[] = []

  let current = queue.shift()
  while (current !== undefined) {
    topOrder.push(current)
    for (const child of outgoingTargets.get(current) ?? []) {
      const deg = (inDegree.get(child) ?? 0) - 1
      inDegree.set(child, deg)
      if (deg === 0) queue.push(child)
    }
    current = queue.shift()
  }

  const yTop = new Map<string, number>()
  for (const id of topOrder) {
    const predecessors = incomingSources.get(id) ?? []
    if (predecessors.length === 0) {
      yTop.set(id, 0)
    } else {
      const maxPredBottom = Math.max(
        ...predecessors.map((pId) => {
          const pY = yTop.get(pId) ?? 0
          const pH = heights[pId] ?? defaultNodeHeight
          return pY + pH
        })
      )
      yTop.set(id, maxPredBottom + ranksep)
    }
  }

  return yTop
}

/**
 * High edge weight for edges leaving conditionIf / conditionElse nodes.
 * This tells dagre to strongly prefer keeping children vertically aligned
 * under their branch, preventing drift caused by asymmetric sibling branches.
 */
const CONDITION_BRANCH_EDGE_WEIGHT = 10

export type Options = {
  rankdir?: 'TB' | 'BT' | 'LR' | 'RL'
  nodeWidth: number
  defaultNodeHeight?: number
  nodesep?: number
  ranksep?: number
  fitViewMaxZoom?: number
}

export const useDagreAutoLayout = (
  nodes: NodeUI[],
  edges: EdgeUI[],
  setNodes: Dispatch<SetStateAction<NodeUI[]>>,
  options: Options
) => {
  const rankdir = options?.rankdir ?? 'TB'
  const nodeWidth = options.nodeWidth
  const defaultNodeHeight = options?.defaultNodeHeight ?? 60
  const fitViewMaxZoom = options?.fitViewMaxZoom
  const nodesep = options?.nodesep ?? 40
  const ranksep = options?.ranksep ?? 200

  const [nodeHeights, setNodeHeights] = useState<Record<string, number>>({})
  const [isLaidOut, setIsLaidOut] = useState<boolean>(false)
  const nodesInitialized = useNodesInitialized()
  const { fitView } = useReactFlow()

  const layoutWithDagre = useCallback(
    (
      nodesToLayout: NodeUI[],
      edgesToLayout: EdgeUI[],
      heights: Record<string, number>
    ): NodeUI[] => {
      const graph = new dagre.graphlib.Graph()
      graph.setGraph({ rankdir, nodesep, ranksep, ranker: 'network-simplex' })
      graph.setDefaultEdgeLabel(() => ({}))

      const nodeMap = new Map(nodesToLayout.map((n) => [n.id, n]))

      nodesToLayout.forEach((n) => {
        const height = heights[n.id] ?? defaultNodeHeight
        graph.setNode(n.id, { width: nodeWidth, height })
      })
      edgesToLayout.forEach((e) => {
        const sourceType = nodeMap.get(e.source)?.type
        const isBranchEdge = sourceType === 'conditionIf' || sourceType === 'conditionElse'
        graph.setEdge(e.source, e.target, {
          minlen: 1,
          weight: isBranchEdge ? CONDITION_BRANCH_EDGE_WEIGHT : 1,
        })
      })

      dagre.layout(graph)

      // Reorder condition branches by priority (else last)
      reorderConditionBranches(graph, nodesToLayout, edgesToLayout)

      // Push apart branches whose subtrees overlap (e.g. nested conditions)
      resolveConditionBranchOverlaps(graph, nodesToLayout, edgesToLayout, nodesep)

      const yPositions = computeConsistentYPositions(
        edgesToLayout,
        nodesToLayout.map((n) => n.id),
        heights,
        defaultNodeHeight,
        ranksep
      )

      return nodesToLayout.map((n) => {
        const dagreNode = graph.node(n.id)
        if (!dagreNode) return n
        return {
          ...n,
          position: {
            x: dagreNode.x - dagreNode.width / 2,
            y: yPositions.get(n.id) ?? dagreNode.y - dagreNode.height / 2,
          },
          sourcePosition: rankdir === 'TB' || rankdir === 'BT' ? Position.Bottom : Position.Right,
          targetPosition: rankdir === 'TB' || rankdir === 'BT' ? Position.Top : Position.Left,
        }
      })
    },
    [rankdir, nodeWidth, defaultNodeHeight, nodesep, ranksep]
  )

  const relayout = useCallback(
    (fit = false) => {
      const repositionedNodes = layoutWithDagre(nodes, edges, nodeHeights)
      setNodes(repositionedNodes)
      if (fit) requestAnimationFrame(() => fitView({ padding: 0.2, maxZoom: fitViewMaxZoom }))
    },
    [edges, fitView, fitViewMaxZoom, layoutWithDagre, nodeHeights, nodes, setNodes]
  )

  const onSizeNode = useCallback((id: string, _width: number, height: number) => {
    setNodeHeights((prev) => {
      const current = prev[id]
      if (current && Math.abs(current - height) < 0.5) return prev
      return { ...prev, [id]: height }
    })
  }, [])

  // When nodes are initialized and heights are known, lay out once and fit view
  useEffect(() => {
    if (isLaidOut) return
    if (!nodesInitialized) return
    if (nodes.length === 0) return
    const allMeasured = nodes.every((n) => nodeHeights[n.id] != null)
    if (!allMeasured) return
    relayout(true)
    setIsLaidOut(true)
  }, [nodesInitialized, nodes, nodeHeights, isLaidOut, relayout])

  return useMemo(() => ({ onSizeNode, isLaidOut, relayout }), [onSizeNode, isLaidOut, relayout])
}
